// pipe scan-intake (track D): the React print surface for the label queue. Pure presenters over
// the pure encoders — the barcode SVG comes from code128.ts, the Zebra stream from zpl.ts.
//
// Import discipline (mirrors erp-table-controls.tsx): only @affine/component (Button),
// @affine/i18n, react/react-dom, the pure-view-safe ErpInfoTip, the sibling pure modules and this
// file's css module. NO runtime ErpService / workbench / DI — the intake screen (track C) owns
// the queue state and wires the callbacks.
import { Button } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { useMemo } from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';

import { ErpInfoTip } from '../../desktop/pages/workspace/erp-info-tip';
import { type Code128Svg, toSvgPath } from './code128';
import * as styles from './label-sheet.css';
import { type LabelItem, zplForQueue } from './zpl';

// Barcode drawing height in SVG user units (the x axis is modules); the label cell then scales
// the whole SVG to the space left between the text lines.
const BARCODE_SVG_HEIGHT = 40;

function LabelCell({ item }: { item: LabelItem }) {
  const svg = useMemo<Code128Svg | null>(() => {
    try {
      return toSvgPath(item.code, { height: BARCODE_SVG_HEIGHT });
    } catch {
      // Unencodable code (non-ASCII / empty): keep the text lines so the label still identifies
      // the item instead of dropping it from the sheet silently.
      return null;
    }
  }, [item.code]);
  return (
    <div className={styles.cell}>
      {item.title ? <div className={styles.titleLine}>{item.title}</div> : null}
      {svg ? (
        <svg
          className={styles.barcode}
          viewBox={svg.viewBox}
          preserveAspectRatio="none"
          shapeRendering="crispEdges"
          role="img"
          aria-label={item.code}
        >
          <path d={svg.path} fill="currentColor" />
        </svg>
      ) : null}
      <div className={styles.codeLine}>{item.code}</div>
      {item.subtitle ? (
        <div className={styles.subtitleLine}>{item.subtitle}</div>
      ) : null}
    </div>
  );
}

export interface LabelSheetProps {
  items: readonly LabelItem[];
}

/** The printable grid: one 2.25in × 1.25in cell per queued label (`page-break-inside: avoid`, so
 *  a cell never splits across pages). Rendered on demand by `printLabels`, but exported so a
 *  screen can also embed it as an on-screen preview. */
export function LabelSheet({ items }: LabelSheetProps) {
  return (
    <div className={styles.sheet}>
      {items.map((item, index) => (
        <LabelCell key={`${index}-${item.code}`} item={item} />
      ))}
    </div>
  );
}

/** Render the queue into a print-only portal under <body> and open the system print dialog.
 *  While the portal is mounted, the print stylesheet shows ONLY the sheet (the app is hidden);
 *  the portal is torn down after printing, so ordinary page prints are untouched. */
export function printLabels(items: readonly LabelItem[]): void {
  if (items.length === 0) return;
  const host = document.createElement('div');
  host.className = styles.printRoot;
  document.body.append(host);
  const root = createRoot(host);
  // Synchronous render: the sheet must be in the DOM before the (blocking) print() call snapshots
  // the page. SVG + system fonts need no async loading, so no settle frame is required.
  flushSync(() => {
    root.render(<LabelSheet items={items} />);
  });
  let done = false;
  const cleanup = () => {
    if (done) return;
    done = true;
    window.removeEventListener('afterprint', cleanup);
    root.unmount();
    host.remove();
  };
  window.addEventListener('afterprint', cleanup);
  window.print();
  // Engines where print() blocks have fired afterprint by now; where the dialog is asynchronous
  // (mobile webviews) this is a leak guard well past any real print interaction.
  setTimeout(cleanup, 60_000);
}

/** Download the queue as a `labels.zpl` file — one ^XA…^XZ label per item — for sending straight
 *  to a Zebra printer (lp / netcat / Zebra Setup Utilities). */
export function downloadZpl(
  items: readonly LabelItem[],
  filename = 'labels.zpl'
): void {
  if (items.length === 0) return;
  const blob = new Blob([zplForQueue(items)], {
    type: 'application/octet-stream',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export interface LabelQueuePanelProps {
  /** How many labels are queued — the panel is a pure presenter, the screen owns the queue. */
  count: number;
  onPrint: () => void;
  onDownloadZpl: () => void;
  onClear: () => void;
}

/** The queue toolbar consumed by the intake screen: count + print / ZPL / clear, all disabled on
 *  an empty queue. Strings are the pre-added com.affine.pipe.labels.* keys. */
export function LabelQueuePanel({
  count,
  onPrint,
  onDownloadZpl,
  onClear,
}: LabelQueuePanelProps) {
  const t = useI18n();
  const empty = count === 0;
  return (
    <div className={styles.queuePanel}>
      <span className={styles.queueCount}>
        {t['com.affine.pipe.labels.queue']({ count: String(count) })}
        <ErpInfoTip
          content={t['com.affine.pipe.intake.tooltip.queue']()}
          data-testid="intake-queue-info-tip"
        />
      </span>
      <Button variant="primary" disabled={empty} onClick={onPrint}>
        {t['com.affine.pipe.labels.print']()}
      </Button>
      <Button disabled={empty} onClick={onDownloadZpl}>
        {t['com.affine.pipe.labels.zpl']()}
      </Button>
      <Button variant="plain" disabled={empty} onClick={onClear}>
        {t['com.affine.pipe.labels.clear']()}
      </Button>
    </div>
  );
}
